import React from "react";

const Row = ({ label, children }) => (
  <div className="flex items-center gap-2 mb-2">
    <span className="text-[10px] text-white/45 w-24 shrink-0 uppercase tracking-wide truncate">{label}</span>
    {children}
  </div>
);

const Txt = ({ value, onChange, placeholder, testid }) => {
  const [v, setV] = React.useState(value || "");
  const focused = React.useRef(false);
  React.useEffect(() => { if (!focused.current) setV(value || ""); }, [value]);
  const commit = (val) => { if (val !== (value || "")) onChange(val); };
  return (
    <input value={v} placeholder={placeholder} data-testid={testid}
      onFocus={() => { focused.current = true; }}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { focused.current = false; commit(v); }}
      onKeyDown={(e) => { if (e.key === "Enter") commit(v); }}
      className="flex-1 min-w-0 glass-card px-2 py-1.5 bg-black/40 text-white text-[11px] outline-none" />
  );
};

const Color = ({ value, onChange, testid }) => (
  <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className="w-8 h-7 rounded bg-transparent border border-white/15 cursor-pointer shrink-0" />
);

const toHex = (c) => {
  if (!c || !c.startsWith("rgb")) return c || "";
  const n = c.match(/\d+/g) || [];
  return "#" + n.slice(0, 3).map((x) => Number(x).toString(16).padStart(2, "0")).join("");
};

/** Estilos del elemento seleccionado: color, tipografía, espaciado, bordes… */
export const StylePanel = ({ el, onChange }) => {
  if (!el) return <p className="text-xs text-white/40 p-4">Toca un elemento de la página.</p>;
  const cs = el.ownerDocument.defaultView.getComputedStyle(el);
  const set = (prop, val) => { el.style[prop] = val; onChange(); };
  const get = (prop) => el.style[prop] || cs[prop];

  return (
    <div className="p-3" data-testid="style-panel">
      <p className="text-[10px] text-white/30 mb-2">
        Escribe el valor y pulsa Enter (o sal del campo) para aplicarlo.
      </p>
      <Row label="Texto">
        <Color value={toHex(get("color"))} onChange={(v) => set("color", v)} testid="style-color" />
        <Txt value={get("color")} onChange={(v) => set("color", v)} testid="style-color-text" />
      </Row>
      <Row label="Fondo">
        <Color value={toHex(get("backgroundColor"))} onChange={(v) => set("backgroundColor", v)}
          testid="style-bg" />
        <Txt value={get("backgroundColor")} onChange={(v) => set("backgroundColor", v)} />
      </Row>
      <Row label="Tamaño letra">
        <Txt value={get("fontSize")} onChange={(v) => set("fontSize", v)} placeholder="16px"
          testid="style-fontsize" />
        <Txt value={get("fontWeight")} onChange={(v) => set("fontWeight", v)} placeholder="700" />
      </Row>
      <Row label="Fuente">
        <Txt value={get("fontFamily")} onChange={(v) => set("fontFamily", v)} />
      </Row>
      <Row label="Alineado">
        {["left", "center", "right", "justify"].map((a) => (
          <button key={a} onClick={() => set("textAlign", a)} data-testid={`style-align-${a}`}
            className={`matrix-chip ${get("textAlign") === a ? "is-active" : ""} px-2 py-1 rounded text-[10px]`}>
            {a === "left" ? "izq" : a === "center" ? "centro" : a === "right" ? "der" : "just"}
          </button>
        ))}
      </Row>
      <Row label="Relleno"><Txt value={get("padding")} onChange={(v) => set("padding", v)}
        placeholder="10px 20px" testid="style-padding" /></Row>
      <Row label="Margen"><Txt value={get("margin")} onChange={(v) => set("margin", v)}
        placeholder="0 auto" /></Row>
      <Row label="Ancho">
        <Txt value={get("width")} onChange={(v) => set("width", v)} placeholder="auto" />
        <Txt value={get("height")} onChange={(v) => set("height", v)} placeholder="auto" />
      </Row>
      <Row label="Bordes">
        <Txt value={get("borderRadius")} onChange={(v) => set("borderRadius", v)} placeholder="12px"
          testid="style-radius" />
        <Txt value={get("border")} onChange={(v) => set("border", v)} placeholder="1px solid #000" />
      </Row>
      <Row label="Sombra"><Txt value={get("boxShadow")} onChange={(v) => set("boxShadow", v)}
        placeholder="0 4px 12px rgba(0,0,0,.3)" /></Row>
      <Row label="Opacidad">
        <input type="range" min="0" max="1" step="0.05" data-testid="style-opacity"
          value={parseFloat(get("opacity")) || 1} onChange={(e) => set("opacity", e.target.value)}
          className="flex-1" />
        <span className="text-[10px] text-white/50 w-8">{get("opacity")}</span>
      </Row>
    </div>
  );
};
